import React, { Component } from 'react';
import autoBind from 'react-autobind/src/autoBind';
import TableHelper from 'src/utils/table-helper';
import { withTranslation } from 'react-i18next';
import composeModalActions from 'src/utils/modal-action-wrapper';
import ErrorMessage from 'src/components/misc/error-message';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import AdminRatingReviewModal from 'src/components/admin/rating-reviews/components/admin-rating-review-modal';
import { Entities } from 'src/common/index';
import BootstrapTableErrorBoundary from 'src/components/error/BootstrapTableErrorBoundary';
import CircularProgress from 'material-ui/CircularProgress';

export const reviewAdministrationFilterMappings = t => ({
    ALL_REVIEWS: {
        filter: 'ALL_REVIEWS',
        title: t('administration.task_ratings.ratings'),
    },
    commentsRequireReview: {
        filter: 'REVIEW_NEEDS_APPROVAL',
        title: t('administration.task_ratings.approve_reviews'),
    },
    answersRequireReview: {
        filter: 'ANSWER_NEEDS_APPROVAL',
        title: t('administration.task_ratings.approve_answer'),
    },
});

class RatingReviewAdministration extends Component {
    constructor(props) {
        super(props);
        autoBind(this);
        const { t } = props;

        const query = props.location.query;
        const filter = query ? query.filter : undefined;
        const filterOrDefault = filter ? filter : 'ALL_REVIEWS';
        const filterMappings = reviewAdministrationFilterMappings(t);
        const currentFilterMapping = filterMappings[filterOrDefault];

        this.tableHelper = new TableHelper();
        this.state = {
            tableData: [],
            filter: currentFilterMapping.filter,
            tableTitle: currentFilterMapping.title,
            startedFetching: false,
        };
    }

    componentWillMount() {
        this.props.fetchReviewsByFilterAdmin(this.state.filter);
        this.props.fetchAll(Entities.REVIEW_CATEGORY);
    }

    componentWillReceiveProps(nextProps) {
        const { t } = this.props;
        const { reviews, reviewCategories, selectedCompany } = nextProps;

        const query = nextProps.location.query;
        const filter = query ? query.filter : undefined;
        const filterOrDefault = filter ? filter : 'ALL_REVIEWS';
        const filterMappings = reviewAdministrationFilterMappings(t);
        const currentFilterMapping = filterMappings[filterOrDefault];

        if (currentFilterMapping.filter !== this.state.filter) {
            this.setState({
                filter: currentFilterMapping.filter,
                tableTitle: currentFilterMapping.title,
            });
            this.props.fetchReviewsByFilterAdmin(currentFilterMapping.filter);
        }
        if (reviews.isFetching && !this.state.startedFetching) this.setState({ startedFetching: true });

        switch (reviews.action.type) {
            case 'UPDATE': {
                this.setState({ tableData: this.populateTableData(reviews) });
                break;
            }
            case 'FETCH': {
                this.setState({ tableData: this.populateTableData(reviews) });
                break;
            }
            default: {
                console.error('Unexpected case:', reviews.action.type);
            }
        }

        this.tableHelper.processPagination(reviews.items.length);
    }

    populateTableData(reviews) {
        return reviews.items
            .filter(review => {
                switch (this.state.filter) {
                    case 'ANSWER_NEEDS_APPROVAL':
                        return review.answerStatus === 'SUBMITTED';
                    case 'REVIEW_NEEDS_APPROVAL':
                        return review.visible === false;
                    default:
                        return true;
                }
            })
            .map(review => {
                return {
                    id: review.id,
                    taskNumber: review.task.identifier,
                    comment: review.comment,
                    operatorAnswer: review.operatorAnswer,
                    task: review.task,
                    customer: review.customer,
                    visible: review.visible,
                    answerStatus: review.answerStatus === 'SUBMITTED' || review.answerStatus === 'REVIEWED',
                };
            });
    }

    render() {
        const { reviews, t } = this.props;
        const finishFetching = this.state.startedFetching && !reviews.isFetching;
        if (finishFetching) {
            return (
                <div>
                    <legend className="legend">{this.state.tableTitle}</legend>
                    {(() => {
                        if (reviews.error) return <ErrorMessage object={reviews} />;
                        else
                            return (
                                <BootstrapTableErrorBoundary>
                                    <BootstrapTable
                                        ref={ref => (this.table = ref)}
                                        data={this.state.tableData}
                                        exportCSV={true}
                                        striped={true}
                                        hover={true}
                                        condensed={true}>
                                        <TableHeaderColumn
                                            columnTitle={true}
                                            dataField="id"
                                            dataAlign="left"
                                            width="10"
                                            dataSort={true}
                                            isKey={true}
                                            export={true}>
                                            {t('common_phrases.id')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            columnTitle={true}
                                            dataField="taskNumber"
                                            dataAlign="left"
                                            width="20"
                                            dataSort={true}
                                            export={true}>
                                            {t('administration.task_ratings.task_number')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            columnTitle={true}
                                            dataField="comment"
                                            dataAlign="left"
                                            width="100"
                                            dataSort={true}
                                            export={true}>
                                            {t('administration.task_ratings.comment')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            columnTitle={true}
                                            dataField="operatorAnswer"
                                            dataAlign="left"
                                            width="100"
                                            dataSort={true}
                                            export={true}
                                            hidden={this.state.filter === 'REVIEW_NEEDS_APPROVAL'}>
                                            {t('administration.task_ratings.answer')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            columnTitle={false}
                                            dataField="visible"
                                            dataAlign="center"
                                            width="20"
                                            dataSort={true}
                                            dataFormat={this.getReviewVisibilityFormat}
                                            export={true}
                                            hidden={this.state.filter === 'ANSWER_NEEDS_APPROVAL'}>
                                            {t('administration.task_ratings.visible')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            columnTitle={false}
                                            dataField="answerStatus"
                                            dataAlign="center"
                                            width="20"
                                            dataSort={true}
                                            dataFormat={this.getAnsweredVisibilityFormat}
                                            export={true}
                                            hidden={this.state.filter === 'ANSWER_NEEDS_APPROVAL'}>
                                            {t('administration.task_ratings.answered')}
                                        </TableHeaderColumn>
                                        <TableHeaderColumn
                                            dataField="actions"
                                            width="30"
                                            dataFormat={this.tableActionsFormatter}
                                            export={false}
                                            hidden={!this.state.filter || this.state.filter === 'ALL_REVIEWS'}>
                                            {t('common_phrases.actions')}
                                        </TableHeaderColumn>
                                    </BootstrapTable>
                                </BootstrapTableErrorBoundary>
                            );
                    })()}
                </div>
            );
        } else return <CircularProgress />;
    }

    getAnsweredVisibilityFormat(cell) {
        const { t } = this.props;
        return cell === true ? (
            <span
                title={t('administration.task_ratings.answered')}
                className="glyphicon glyphicon-ok-circle text-success"
            />
        ) : (
            <span
                title={t('administration.task_ratings.unanswered')}
                className="fa fa-times-circle-o text-danger fa-lg"
            />
        );
    }

    getReviewVisibilityFormat(cell, row) {
        const { t } = this.props;
        return cell === true ? (
            <span
                title={t('administration.task_ratings.visible')}
                className="glyphicon glyphicon-ok-circle text-success"
            />
        ) : (
            <span
                title={t('administration.task_ratings.under_review')}
                className="fa fa-times-circle-o text-danger fa-lg"
            />
        );
    }

    openEditModal(review) {
        const { t, i18n } = this.props;
        const reviewView = this.state.filter === 'REVIEW_NEEDS_APPROVAL';
        this.props.openModal({
            component: AdminRatingReviewModal,
            componentProps: {
                id: review.id,
                review: review,
                reviewCategories: this.props.reviewCategories,
                filter: this.state.filter,
                i18n: i18n,
            },
            title: t('common_phrases.rating'),
            successButtonLabel: reviewView
                ? t('administration.task_ratings.approve_review')
                : t('administration.task_ratings.approve_answer'),
            cancelButtonLabel: t('common_phrases.abort'),
        });
    }

    tableActionsFormatter(cell, review) {
        const { t } = this.props;
        return (
            <div>
                {this.state.filter && this.state.filter !== 'ALL_REVIEWS' && (
                    <button
                        type="button"
                        className="btn btn-xs transparent"
                        title={t('user_functionality.edit')}
                        onClick={() => {
                            this.openEditModal(review);
                        }}>
                        <span className="glyphicon glyphicon-pencil text-info" />
                    </button>
                )}
            </div>
        );
    }
}

export default withTranslation()(composeModalActions(RatingReviewAdministration));
